import dynamic from "next/dynamic"
import React from "react"
import classNames from "classnames"
import { ProductLifeCycleState } from "@dustin-web/microsite-data/src/graphql/product/generated/graphql-types"
import { useSiteContext, useTranslations } from "@dustin-web/microsite-components/src/hooks"
import type { GenericAvailabilityType } from "../cards"
import { getStockAvailability } from "./service/stock"
import { getAvailabilityType } from "./service/availability-type"
import { getShippingAvailability } from "./service/shipping"

const ProductAvailability = dynamic(() => import('./product-availability'))
const ProductAvailabilityList = dynamic(() => import('./product-availability-list'))

const bannedLifeCycleStatesForPage: ProductLifeCycleState[] = [
  ProductLifeCycleState.EndOfLife,
  ProductLifeCycleState.TempStopped,
  ProductLifeCycleState.NotReleased,
]

type Props = {
  availability?: GenericAvailabilityType | null
  isBid?: boolean
  listForm?: boolean
  compactStatus?: boolean
  showOnlyIcon?: boolean
  className?: string
  defaultContainerClassName?: string
  stockClassName?: string
  infoTextClassName?: string
  iconClassName?: string
  availabilityTextClassName?: boolean
  containerClassNames?: string
  deliveryContainerClassName?: string
}

const ProductAvailabilityContainer = ({
  isBid = false,
  listForm = false,
  showOnlyIcon = false,
  compactStatus = false,
  availability,
  className,
  defaultContainerClassName,
  stockClassName,
  infoTextClassName,
  deliveryContainerClassName,
  containerClassNames,
  availabilityTextClassName,
  iconClassName,
}: Props) => {
  const { translate } = useTranslations()
  const { sourcingCompany } = useSiteContext()

  if (
    !availability ||
    !availability.productLifeCycleState ||
    bannedLifeCycleStatesForPage.includes(availability.productLifeCycleState)
  ) {
    return
  }

  const availabilityType = getAvailabilityType(availability)
  const stock = getStockAvailability(translate, availability, availabilityType, isBid, sourcingCompany === 'NL')
  const shippingText = getShippingAvailability(translate, availabilityType)

  if (!stock) {
    return
  }

  const bidInfo = isBid && translate('availabilitytext_bid_info') || ''

  if (listForm) {
    return (
      <ProductAvailabilityList
        shippingText={shippingText}
        color={stock.iconColor}
        iconClassName={iconClassName}
        stockText={stock.translation}
        stockTooltip={stock.tooltip}
        className={className}
        showOnlyIcon={showOnlyIcon}
        defaultContainerClassName={defaultContainerClassName}
        containerClassNames={classNames(containerClassNames, { 'u-pt-8': listForm })}
        compactStatus={compactStatus}
      />
    )
  }

  return (
    <ProductAvailability
      isBid={isBid}
      bidInfoText={bidInfo}
      color={stock.iconColor}
      stockText={stock.tooltip || stock.translation}
      shippingText={shippingText}
      showIsBidInfoText={isBid}
      className={className}
      defaultContainerClassName={defaultContainerClassName}
      stockClassName={stockClassName}
      infoTextClassName={infoTextClassName}
      deliveryContainerClassName={deliveryContainerClassName}
      containerClassNames={containerClassNames}
      useAvailabilityStockTextClassName={availabilityTextClassName}
      iconClassName={iconClassName}
    />
  )
}

export default ProductAvailabilityContainer 
